/**
 * Global UI state + imperative API for code outside the component tree
 * (input controller, game session, network layer).
 *
 *   uiStore.openRadialMenu({ x, y, tile, ownerID })   right-click / long-press menu
 *   uiStore.showPlayerTooltip({ x, y, playerID })      hover tooltip (call on mouse move)
 *   uiStore.hidePlayerTooltip()
 *   uiStore.openPlayerPanel(playerID)
 *   uiStore.toast({ text, kind })                      transient notification
 *   uiStore.confirm({ title, message }) → Promise<boolean>
 */
import { batch, signal } from "@preact/signals";
import type { PlayerID, TileRef, UnitID } from "../../core/types";

export type ToastKind = "info" | "success" | "warning" | "danger";

export interface Toast {
  id: number;
  kind: ToastKind;
  /** Already translated text. */
  text: string;
  /** Optional secondary line. */
  detail?: string;
  /** Icon name from icons.tsx */
  icon?: string;
  /** Auto-dismiss after ms (0 = sticky). */
  timeout: number;
  createdAt: number;
}

export interface RadialMenuRequest {
  /** Screen (client) coordinates in CSS pixels. */
  x: number;
  y: number;
  tile: TileRef;
  /** Owner of the tile (0 = unowned / water). */
  ownerID: PlayerID;
  /** Own structure / unit under the cursor, enables upgrade / demolish actions. */
  unitID?: UnitID;
  /** Wave 4 stage J (controls): open straight in a submenu (smart click: "build"; Shift+E: "emoji"). */
  submenu?: "build" | "emoji";
}

export interface PlayerTooltipRequest {
  x: number;
  y: number;
  playerID: PlayerID;
  tile?: TileRef;
}

export interface ConfirmRequest {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export type ModalKind = "settings" | "help" | "flagPicker" | "hotkeys";

interface ConfirmState extends ConfirmRequest {
  resolve(ok: boolean): void;
}

export const toasts = signal<Toast[]>([]);
export const radialMenu = signal<RadialMenuRequest | null>(null);
export const playerTooltip = signal<PlayerTooltipRequest | null>(null);
export const playerPanel = signal<PlayerID | null>(null);
export const modal = signal<ModalKind | null>(null);
export const confirmDialog = signal<ConfirmState | null>(null);
/** Chat input focus request counter (Enter hotkey). */
export const chatFocusRequest = signal(0);
/** True when the screen matches a media query (false outside a browser). */
function screenMatches(query: string): boolean {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

/**
 * HUD panel visibility toggles (keyboard + buttons). Stage M uxpolish: phones and tablets start
 * with the leaderboard and the event log collapsed so the map stays visible; short laptop screens
 * also start with the log collapsed (refused orders show as toasts, so nothing important is missed).
 */
export const hudPanels = signal({
  leaderboard: !screenMatches("(max-width: 1024px), (max-height: 700px)"),
  log: !screenMatches("(max-width: 1024px), (max-height: 900px)"),
  chat: false,
});

let toastSeq = 1;
const toastTimers = new Map<number, ReturnType<typeof setTimeout>>();

function dismissToast(id: number): void {
  const timer = toastTimers.get(id);
  if (timer) clearTimeout(timer);
  toastTimers.delete(id);
  toasts.value = toasts.value.filter((t) => t.id !== id);
}

export const uiStore = {
  toast(t: { text: string; kind?: ToastKind; detail?: string; icon?: string; timeout?: number }): number {
    const id = toastSeq++;
    const toast: Toast = {
      id,
      kind: t.kind ?? "info",
      text: t.text,
      detail: t.detail,
      icon: t.icon,
      timeout: t.timeout ?? 4500,
      createdAt: Date.now(),
    };
    // Keep at most 5 toasts on screen.
    const list = [...toasts.value, toast];
    while (list.length > 5) {
      const old = list.shift();
      if (old) {
        const timer = toastTimers.get(old.id);
        if (timer) clearTimeout(timer);
        toastTimers.delete(old.id);
      }
    }
    toasts.value = list;
    if (toast.timeout > 0) toastTimers.set(id, setTimeout(() => dismissToast(id), toast.timeout));
    return id;
  },

  dismissToast,

  openRadialMenu(req: RadialMenuRequest): void {
    batch(() => {
      playerTooltip.value = null;
      radialMenu.value = { ...req };
    });
  },

  closeRadialMenu(): void {
    radialMenu.value = null;
  },

  isRadialMenuOpen(): boolean {
    return radialMenu.value !== null;
  },

  showPlayerTooltip(req: PlayerTooltipRequest): void {
    if (radialMenu.value) return;
    const cur = playerTooltip.value;
    if (cur && cur.playerID === req.playerID && Math.abs(cur.x - req.x) < 2 && Math.abs(cur.y - req.y) < 2) return;
    playerTooltip.value = req.playerID > 0 ? { ...req } : null;
  },

  hidePlayerTooltip(): void {
    if (playerTooltip.value) playerTooltip.value = null;
  },

  openPlayerPanel(id: PlayerID): void {
    batch(() => {
      radialMenu.value = null;
      playerPanel.value = id;
    });
  },

  closePlayerPanel(): void {
    playerPanel.value = null;
  },

  openModal(kind: ModalKind): void {
    modal.value = kind;
  },

  closeModal(): void {
    modal.value = null;
  },

  confirm(req: ConfirmRequest): Promise<boolean> {
    // Resolve any pending dialog as cancelled.
    confirmDialog.value?.resolve(false);
    return new Promise<boolean>((resolve) => {
      confirmDialog.value = {
        ...req,
        resolve: (ok: boolean) => {
          confirmDialog.value = null;
          resolve(ok);
        },
      };
    });
  },

  focusChat(): void {
    hudPanels.value = { ...hudPanels.value, chat: true };
    chatFocusRequest.value++;
  },

  togglePanel(panel: keyof typeof hudPanels.value, force?: boolean): void {
    const cur = hudPanels.value;
    hudPanels.value = { ...cur, [panel]: force ?? !cur[panel] };
  },

  /** Close the top-most transient overlay. Returns true if something was closed. */
  closeTopmost(): boolean {
    if (confirmDialog.value) {
      confirmDialog.value.resolve(false);
      return true;
    }
    if (radialMenu.value) {
      radialMenu.value = null;
      return true;
    }
    if (modal.value) {
      modal.value = null;
      return true;
    }
    if (playerPanel.value !== null) {
      playerPanel.value = null;
      return true;
    }
    return false;
  },

  /** Reset all in-game overlays (when leaving a game). */
  resetGameOverlays(): void {
    batch(() => {
      radialMenu.value = null;
      playerTooltip.value = null;
      playerPanel.value = null;
    });
  },
};
